import { useMemo } from "react";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import type {
    VideoCaptionHit,
    VideoCaptionSearchResult,
    VideoPlatform,
} from "@/lib/video-types";
import { apiGet } from "@/lib/api-client";

/**
 * One character is not a search. "a" matches a fifth of every transcript, the
 * query comes back with a full page, and the reader immediately types the next
 * letter — so the request is not worth making.
 */
export const CAPTION_SEARCH_MIN_LENGTH = 2;

/** Clips per request. The RPC clamps this server-side as well. */
export const CAPTION_SEARCH_PAGE_SIZE = 20;

interface CaptionSearchRow {
    video_row_id: string;
    source: "library" | "imported";
    platform: VideoPlatform;
    platform_video_id: string;
    title: string;
    thumbnail_url: string | null;
    video_url: string;
    duration_seconds: number | null;
    start_seconds: number;
    end_seconds: number;
    caption_text: string;
    rank: number;
    total_matches: number | string;
}

interface CaptionSearchPage extends VideoCaptionSearchResult {
    offset: number;
}

function toCaptionHit(row: CaptionSearchRow): VideoCaptionHit {
    return {
        videoRowId: row.video_row_id,
        source: row.source,
        videoId: row.platform_video_id,
        platform: row.platform,
        title: row.title,
        videoUrl: row.video_url,
        thumbnailUrl: row.thumbnail_url ?? undefined,
        duration: row.duration_seconds ?? undefined,
        start: Number(row.start_seconds),
        end: Number(row.end_seconds),
        text: row.caption_text,
        rank: Number(row.rank ?? 0),
    };
}

/**
 * One page of caption hits. Shared by the infinite search and by the topic
 * band's per-word lookups, so both speak to the API the same way.
 */
async function fetchCaptionSearchPage(params: {
    query: string;
    source?: "library" | "imported";
    limit: number;
    offset: number;
}): Promise<CaptionSearchPage> {
    const query = new URLSearchParams({
        q: params.query,
        limit: String(params.limit),
        offset: String(params.offset),
        ...(params.source ? { source: params.source } : {}),
    });

    const rows =
        (await apiGet<CaptionSearchRow[]>(`/v1/videos/captions?${query.toString()}`)) ?? [];

    return {
        hits: rows.map(toCaptionHit),
        // `total_matches` is a window over the full de-duplicated set, so every
        // row carries the same figure — including the first one.
        total: rows.length > 0 ? Number(rows[0].total_matches) : 0,
        offset: params.offset,
    };
}

/**
 * Caption-level search: "which video says this word, and at what second?"
 *
 * The heavy lifting is in `search_captions` (migration
 * `20261031000000_youtube_caption_search.sql`): it matches whole words and
 * adjacent phrases against a tsvector index of every caption line, then keeps
 * the best line per video so a page of results is not one talk repeated twenty
 * times. `total` therefore counts *videos*, which is what the count line says.
 *
 * It searches two corpora at once — the curated `videos` library and the
 * YouTube channel importer's corpus — and tags each hit with `source`. The
 * filter matters here rather than being a nicety: 100 imported TED talks outrank
 * a six-video library for almost any word, so "only published videos" would
 * otherwise be unreachable.
 *
 * Paged with offset rather than fetched in one go: a common word matches
 * thousands of videos, and the reader almost never scrolls past the first
 * screen.
 */
export type CaptionSearchSource = "all" | "library" | "imported";

export function useVideoCaptionSearch(
    query: string,
    source: CaptionSearchSource = "all",
    pageSize = CAPTION_SEARCH_PAGE_SIZE,
) {
    const trimmed = query.trim();
    const enabled = trimmed.length >= CAPTION_SEARCH_MIN_LENGTH;
    // `undefined` means "both corpora" — the RPC treats a NULL p_source that way.
    const sourceFilter = source === "all" ? undefined : source;

    const result = useInfiniteQuery({
        queryKey: queryKeys.videos.captionSearch(trimmed, sourceFilter),
        enabled,
        initialPageParam: 0,
        queryFn: async ({ pageParam }): Promise<CaptionSearchPage> =>
            fetchCaptionSearchPage({
                query: trimmed,
                source: sourceFilter,
                limit: pageSize,
                offset: pageParam as number,
            }),
        getNextPageParam: (lastPage) =>
            lastPage.offset + pageSize < lastPage.total
                ? lastPage.offset + pageSize
                : undefined,
        // Paging back to a search you just looked at should not re-run it, but a
        // transcript added a minute ago should appear without a hard refresh.
        staleTime: 1000 * 60,
        refetchOnWindowFocus: false,
        // No `placeholderData`: unlike a filtered deck, a result here *is* the
        // answer to what was typed, so showing the previous query's rows (with
        // none of the new words highlighted) while the next one loads reads as a
        // broken search. The list clears and the spinner stands in.
    });

    return {
        hits: result.data?.pages.flatMap((page) => page.hits) ?? [],
        total: result.data?.pages[0]?.total ?? 0,
        /** True only for a request that could actually run. */
        isLoading: enabled && result.isLoading,
        isFetching: result.isFetching,
        isError: result.isError,
        error: result.error,
        /** Whether the typed value was long enough to search. */
        isSearchable: enabled,
        hasMore: result.hasNextPage,
        loadMore: result.fetchNextPage,
        isLoadingMore: result.isFetchingNextPage,
    };
}

/**
 * How many words of one topic the band looks up.
 *
 * One request per word, so this is a fan-out budget rather than a display
 * limit: ten lookups answer in a beat and cover a normal deck. A topic with
 * thirty words says so instead of firing thirty queries at a page that is
 * already rendering.
 */
export const TOPIC_CLIPS_MAX_WORDS = 10;

export interface WordCaptionClip {
    /** The topic word this clip was found for, as the deck spells it. */
    word: string;
    hit: VideoCaptionHit;
}

/** The single best line for one word, or `null` when nothing says it. */
async function fetchTopClipForWord(word: string): Promise<VideoCaptionHit | null> {
    const page = await fetchCaptionSearchPage({ query: word, limit: 1, offset: 0 });
    return page.hits[0] ?? null;
}

/**
 * "Hear this topic's words in real videos" — one best clip per vocabulary word.
 *
 * Deliberately one query per word rather than one OR-ed phrase: a joined query
 * ranks by term frequency, so a deck of twelve words comes back as ten lines of
 * "the" and nothing for the other eleven. Per word means every entry either
 * gets its own moment or is honestly reported as missing — which is what makes
 * the band a map of the deck instead of a second search box.
 *
 * `enabled` is the expansion state: the band is collapsed on the topic page and
 * this is what keeps a topic visit at zero caption requests until asked.
 */
export function useTopicCaptionClips(words: string[], enabled = true) {
    const unique = useMemo(() => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const raw of words) {
            const word = raw.trim();
            // The button only appears when a word is long enough to be a
            // sensible query, and the same rule applies here.
            if (word.length < CAPTION_SEARCH_MIN_LENGTH) continue;
            const key = word.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(word);
        }
        return out;
    }, [words]);

    const selected = useMemo(() => unique.slice(0, TOPIC_CLIPS_MAX_WORDS), [unique]);

    const results = useQueries({
        queries: selected.map((word) => ({
            // Keyed on the word alone so opening a word's dialog reuses the
            // clip the band already fetched.
            queryKey: queryKeys.videos.captionTopClip(word.toLowerCase()),
            queryFn: () => fetchTopClipForWord(word),
            enabled,
            staleTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
        })),
    });

    // At most ten entries, so this is cheaper than memoizing on an array that
    // React Query rebuilds on every render.
    const clips: WordCaptionClip[] = selected.flatMap((word, index) => {
        const hit = results[index]?.data;
        return hit ? [{ word, hit }] : [];
    });

    return {
        /** One clip per matched word, in deck order. */
        clips,
        /** Unique, searchable words this topic has. */
        searchableTotal: unique.length,
        /** How many of them were looked up after the fan-out cap. */
        searchedTotal: selected.length,
        /** How many words the cap left out. */
        skippedTotal: Math.max(unique.length - selected.length, 0),
        /** True only while a request that could run is in flight. */
        isLoading: enabled && results.some((result) => result.isLoading),
        /** Every lookup failed — an outage, not an empty corpus. */
        isError: results.length > 0 && results.every((result) => result.isError),
        /** Every lookup has answered, so "no match" is a real answer. */
        isSettled: !enabled || results.every((result) => !result.isPending),
    };
}
